import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { IParkProvider, ParkCatalogIndex, offersOfProvider, providersOf } from '../park-flow/park-catalog';

export interface IParkRoomChoice {
  roomType: any;
  providerId: string;
}

interface IProviderRow {
  provider: IParkProvider;
  offer: any;          // oferta más barata del proveedor para este tipo de habitación
  price: number;
  currency: string;
  refundable: boolean;
  cancelFrom: string;
}

interface IRoomCard {
  roomType: any;
  image: string;
  rows: IProviderRow[];
}

/**
 * Paso 1: tipos de habitación del roomCatalog. Cada tipo muestra una fila por proveedor con su precio "desde";
 * al elegir un proveedor, los pasos siguientes (accesibilidad, tickets, días y comidas) son de ese proveedor.
 */
@Component({
  selector: 'usb-park-rooms',
  templateUrl: './park-rooms.component.html',
  styleUrls: ['./park-rooms.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParkRoomsComponent implements OnChanges {
  @Input() roomTypes: any[] = [];
  @Input() index: ParkCatalogIndex;
  @Input() nights = 0;
  @Input() fallbackImage = '/assets/img/app/hotel_default.png';
  @Input() selectedTypeKey: string | null = null;
  @Input() selectedProviderId: string | null = null;
  @Input() currencies: any[];
  @Input() currencyAgency: any;
  @Input() buttonColorActive: string;
  @Input() buttonColorActiveDark: string;
  @Input() clientSetting: any;

  @Output() choose = new EventEmitter<IParkRoomChoice>();

  cards: IRoomCard[] = [];

  ngOnChanges(): void {
    if (!this.index) {
      this.cards = [];
      return;
    }
    this.cards = (this.roomTypes || [])
      .map(roomType => {
        const rows = providersOf(roomType, this.index)
          .map(provider => {
            const offer = this.index.cheapest(offersOfProvider(roomType, provider.id));
            return offer
              ? {
                  provider,
                  offer,
                  price: this.index.price(offer),
                  currency: this.index.currency(offer),
                  refundable: this.index.isRefundable(offer),
                  cancelFrom: offer.cancelFrom || '',
                }
              : null;
          })
          .filter((r): r is IProviderRow => !!r)
          .sort((a, b) => a.price - b.price);
        const first = rows[0]?.offer;
        return { roomType, image: (first && this.index.image(first)) || this.fallbackImage, rows };
      })
      .filter(card => card.rows.length > 0);
  }

  isSelected(card: IRoomCard, row: IProviderRow): boolean {
    return card.roomType.typeKey === this.selectedTypeKey && row.provider.id === this.selectedProviderId;
  }

  isCardSelected(card: IRoomCard): boolean {
    return card.roomType.typeKey === this.selectedTypeKey;
  }

  pick(card: IRoomCard, row: IProviderRow): void {
    this.choose.emit({ roomType: card.roomType, providerId: row.provider.id });
  }

  trackByType(_: number, card: IRoomCard): string {
    return card.roomType?.typeKey;
  }

  trackByProvider(_: number, row: IProviderRow): string {
    return row.provider.id;
  }
}
